import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import GenericModal from "@ui-components/Modal/GenericModal";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import StatusBadge from "@ui-components/Tag/StatusBadge";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { parseAxiosError } from "@shared/components/utils/apiErrorParser";
import { renewSubscription } from "./requests/subscriptionsApi";
import {
	formatSubscriptionDate,
	getStateBadge,
	parseSubscriptionDate,
	SERVICE_TYPE_LABEL,
} from "./SubscriptionsData";
import { computeRenewalPreview, RENEWAL_TYPE_LABEL } from "./renewalPreview";
import type { RenewalPreview } from "./renewalPreview";
import type { RenewalStartFrom, Subscription } from "./types";

/** Terms offered as one-click choices; anything else goes in the custom field. */
const QUICK_TERMS = [6, 12, 24, 36] as const;

const CURRENCIES = ["USD", "MXN", "EUR"] as const;

interface RenewSubscriptionModalProps {
	subscription: Subscription | null;
	onClose: () => void;
	/** Called after a successful renewal so the table can refetch. */
	onRenewed: () => void;
	/** Injected for deterministic tests; defaults to the real clock. */
	now?: Date;
}

/** One line saying what choosing an anchor would cost, read off its own projection. */
const anchorHint = (projected: RenewalPreview): string => {
	if (projected.termAlreadyEnded) {
		return `Term would have ended ${format(projected.newEnd, "dd MMM yyyy")}`;
	}
	if (projected.gap) {
		return `Leaves ${projected.gap.days} days uncovered`;
	}
	return "Contiguous — no gap";
};

/** Label + value pair used in the context strip and the result panel. */
const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({
	label,
	children,
}) => (
	<div className="flex flex-col min-w-0">
		<span className="text-xs uppercase tracking-wide text-semantic-text-secondary">
			{label}
		</span>
		<span className="text-sm font-semibold mt-1 tabular-nums">{children}</span>
	</div>
);

/**
 * Renew a subscription by term length (CTX-26), and — once it has lapsed —
 * by where that term begins (CTX-522).
 *
 * The anchor is a choice between two dates the backend computes from the
 * current term, not a date picker. A free start/end RANGE stays out for the
 * reason CTX-208 recorded: `renewDate` is recomputed on read as
 * `contractStart + billingCycle`, so an arbitrary end would desynchronize it
 * from `contractEnd`. Choosing only the anchor leaves that identity intact,
 * and keeps every date server-side where no timezone can shift it (CTX-434).
 */
const RenewSubscriptionModal: React.FC<RenewSubscriptionModalProps> = ({
	subscription,
	onClose,
	onRenewed,
	now,
}) => {
	const api = useAxiosAuth0();
	const [termMonths, setTermMonths] = useState<number>(12);
	// `today` is what every renewal did before the anchor existed, so it stays
	// the default: nobody who ignores the control gets a different outcome.
	const [startFrom, setStartFrom] = useState<RenewalStartFrom>("today");
	const [autoRenew, setAutoRenew] = useState<boolean>(false);
	const [purchaseOrder, setPurchaseOrder] = useState<string>("");
	const [currencyCode, setCurrencyCode] = useState<string>("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Frozen per open so the preview can't shift under the operator mid-edit.
	const evaluatedAt = useMemo(() => now ?? new Date(), [now]);

	// Both anchors are projected, not just the selected one: each option shows
	// what CHOOSING it would produce, which is the whole point of the control.
	const previews = useMemo(() => {
		if (!subscription) return null;
		const base = {
			contractEnd: parseSubscriptionDate(subscription.contractEnd),
			termMonths,
			now: evaluatedAt,
		};
		return {
			today: computeRenewalPreview({ ...base, startFrom: "today" as const }),
			term_end: computeRenewalPreview({
				...base,
				startFrom: "term_end" as const,
			}),
		};
	}, [subscription, termMonths, evaluatedAt]);

	if (!subscription || !previews) return null;

	const preview = previews[startFrom];
	const currentBadge = getStateBadge(subscription.state);
	const newBadge = getStateBadge(preview.newState);
	const termIsValid = Number.isInteger(termMonths) && termMonths > 0;
	// The anchor only means something once the term has lapsed: an early or
	// same-day renewal always chains, so offering the choice there would
	// suggest an overlap the backend would refuse to create anyway.
	const anchorIsOffered =
		preview.type === "after_expiry" && subscription.contractEnd !== null;
	const anchorOptions: {
		value: RenewalStartFrom;
		label: string;
		projected: typeof preview;
	}[] = [
		{ value: "term_end", label: "From expiry", projected: previews.term_end },
		{ value: "today", label: "Today", projected: previews.today },
	];
	const canSubmit = termIsValid && !preview.termAlreadyEnded && !submitting;

	const handleRenew = async () => {
		setSubmitting(true);
		setError(null);
		try {
			await renewSubscription(api, subscription.subscriptionId, {
				termMonths,
				// Sent only where it is a real choice, so an early renewal's
				// audit trail does not record an anchor nobody picked.
				startFrom: anchorIsOffered ? startFrom : undefined,
				autoRenew,
				purchaseOrder: purchaseOrder.trim() || null,
				currencyCode: currencyCode || null,
			});
			onRenewed();
			onClose();
		} catch (err) {
			setError(parseAxiosError(err).message);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<GenericModal
			isOpen
			onClose={onClose}
			size="lg"
			customWidth="max-w-4xl"
			title={
				<div className="flex flex-col min-w-0">
					<span className="text-h5 font-bold">Renew subscription</span>
					<span className="text-sm font-normal text-semantic-text-secondary mt-0.5 truncate">
						{subscription.subscriptionId} ·{" "}
						{SERVICE_TYPE_LABEL[subscription.serviceType]} {subscription.serviceName} ·{" "}
						{subscription.customerName}
					</span>
				</div>
			}
			footer={
				<div className="flex items-center gap-2 w-full">
					<span className="text-xs text-semantic-text-secondary truncate">
						The backend recalculates the term when it commits the renewal.
					</span>
					<div className="flex-1" />
					<GenericButton
						label="Cancel"
						variant="secondary"
						size="md"
						onClick={onClose}
						disabled={submitting}
					/>
					<GenericButton
						label="Renew subscription"
						variant="brand"
						size="md"
						loading={submitting}
						disabled={!canSubmit}
						onClick={() => {
							void handleRenew();
						}}
					/>
				</div>
			}
		>
			<div className="flex flex-col gap-5">
				{/* Where the subscription stands right now. */}
				<div className="grid grid-cols-2 sm:grid-cols-3 gap-4 rounded-sm border border-semantic-border-primary bg-semantic-bg-secondary px-4 py-3">
					<Fact label="Current term">
						{formatSubscriptionDate(subscription.contractStart)} →{" "}
						{formatSubscriptionDate(subscription.contractEnd)}
						{subscription.billingCycle ? ` (${subscription.billingCycle} mo)` : ""}
					</Fact>
					<Fact label="Status">
						<StatusBadge variant={currentBadge.variant} label={currentBadge.label} />
					</Fact>
					<Fact label="Covered until today">
						{formatSubscriptionDate(subscription.effectiveEndDate)}
					</Fact>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-5 items-start">
					{/* Term + options */}
					<div className="flex flex-col gap-4">
						<fieldset className="rounded-sm border border-semantic-border-primary p-4 flex flex-col gap-3">
							<legend className="px-1 text-sm font-bold">New term</legend>
							{anchorIsOffered && (
								<div className="flex flex-col gap-2 pb-3 border-b border-dashed border-semantic-border-primary">
									<span className="text-xs uppercase tracking-wide text-semantic-text-secondary">
										Term starts
									</span>
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
										{anchorOptions.map((option) => {
											const selected = startFrom === option.value;
											return (
												<button
													key={option.value}
													type="button"
													onClick={() => setStartFrom(option.value)}
													aria-pressed={selected}
													className={`flex items-start gap-2.5 text-left px-3 py-2.5 rounded-sm border transition-colors ${
														selected
															? "border-semantic-border-brand-secondary bg-semantic-bg-brand-secondary"
															: "border-semantic-border-secondary hover:bg-semantic-state-active"
													}`}
												>
													<span
														className={`flex-none w-4 h-4 mt-0.5 rounded-round border flex items-center justify-center ${
															selected
																? "border-semantic-border-brand-secondary"
																: "border-semantic-border-secondary"
														}`}
													>
														{selected && (
															<span className="w-2 h-2 rounded-round bg-semantic-border-brand-secondary" />
														)}
													</span>
													<span className="min-w-0">
														<span
															className={`block text-sm font-bold ${
																selected ? "text-semantic-text-brand" : ""
															}`}
														>
															{option.label}
														</span>
														<span className="block text-sm tabular-nums">
															{format(option.projected.newStart, "dd MMM yyyy")}
														</span>
														<span
															className={`block text-xs mt-0.5 ${
																option.projected.termAlreadyEnded
																	? "text-semantic-text-danger"
																	: "text-semantic-text-secondary"
															}`}
														>
															{anchorHint(option.projected)}
														</span>
													</span>
												</button>
											);
										})}
									</div>
								</div>
							)}
							<div className="flex flex-wrap gap-2">
								{QUICK_TERMS.map((months) => {
									const selected = termMonths === months;
									return (
										<button
											key={months}
											type="button"
											onClick={() => setTermMonths(months)}
											aria-pressed={selected}
											className={`px-3.5 py-1 rounded-round border text-sm transition-colors ${
												selected
													? "border-semantic-border-brand-secondary bg-semantic-bg-brand-secondary text-semantic-text-brand font-bold"
													: "border-semantic-border-secondary text-semantic-text-primary hover:bg-semantic-state-active"
											}`}
										>
											{months} months
										</button>
									);
								})}
							</div>
							<div className="w-[240px]">
								<GenericInput
									label="Or a custom term"
									type="number"
									value={termMonths}
									unit="months"
									size="small"
									hasError={!termIsValid}
									helperText={
										termIsValid ? undefined : "Enter a whole number of months, 1 or more."
									}
									onChange={(value) => setTermMonths(Number(value))}
								/>
							</div>
							<p className="text-sm text-semantic-text-secondary">
								{anchorIsOffered
									? "The term is counted from the date you pick. From expiry it opens the day after the last covered day, so coverage stays contiguous; from today it starts now and the gap in coverage stays in the audit trail."
									: "The start date is derived from the current term: while the subscription is still running the new term begins the day the current one ends, so coverage never breaks."}
							</p>
						</fieldset>

						<fieldset className="rounded-sm border border-semantic-border-primary p-4 flex flex-col gap-4">
							<legend className="px-1 text-sm font-bold">Options</legend>
							<div className="flex items-center gap-3">
								<div className="flex-1 min-w-0">
									<span className="block text-sm font-semibold">
										Auto-renew at term end
									</span>
									<span className="text-sm text-semantic-text-secondary">
										Applies to the new subscription.
									</span>
								</div>
								<GenericSwitch checked={autoRenew} onChange={setAutoRenew} />
							</div>
							{/* `min-w-0` on both cells: without it a grid item refuses to
								    shrink below its content width and spills over its neighbour. */}
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
									{/* Default size, not `small`: GenericInput hardcodes w-[240px]
									    for the small variant and ignores `fullWidth`, so it
									    overflowed this column and overlapped the currency select. */}
									<div className="min-w-0">
										<GenericInput
											label="Purchase order"
											placeholder="PO-2026-0148"
											value={purchaseOrder}
											onChange={setPurchaseOrder}
										/>
									</div>
									<div className="flex flex-col space-y-2 min-w-0">
										<label
											htmlFor="renew-currency"
											className="text-sm font-medium text-semantic-text-primary"
										>
											Currency
										</label>
										<select
											id="renew-currency"
											value={currencyCode}
											onChange={(event) => setCurrencyCode(event.target.value)}
											className="w-full py-2 px-3 text-sm rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt text-semantic-text-primary"
										>
											<option value="">Not set</option>
											{CURRENCIES.map((code) => (
												<option key={code} value={code}>
													{code}
												</option>
											))}
										</select>
									</div>
								</div>
						</fieldset>
					</div>

					{/* What the renewal will produce. */}
					<div
						className="rounded-sm border border-semantic-border-primary bg-semantic-bg-brand-secondary overflow-hidden"
						aria-live="polite"
					>
						<div className="flex items-center gap-2 px-4 py-3 border-b border-semantic-border-primary">
							<span className="font-bold text-semantic-text-brand flex-1">Result</span>
							<StatusBadge
								variant="info"
								label={RENEWAL_TYPE_LABEL[preview.type]}
								withIcon={false}
							/>
						</div>
						<dl className="flex flex-col">
							<div className="flex items-baseline justify-between gap-4 px-4 py-2.5 border-b border-dashed border-semantic-border-primary">
								<dt className="text-sm text-semantic-text-secondary">New term</dt>
								<dd className="text-sm font-bold tabular-nums text-right">
									{format(preview.newStart, "dd MMM yyyy")} →{" "}
									{format(preview.newEnd, "dd MMM yyyy")}
								</dd>
							</div>
							<div className="flex items-baseline justify-between gap-4 px-4 py-2.5 border-b border-dashed border-semantic-border-primary">
								<dt className="text-sm text-semantic-text-secondary">
									Covered until
								</dt>
								<dd className="text-base font-bold tabular-nums">
									{format(preview.newEffectiveEnd, "dd MMM yyyy")}
								</dd>
							</div>
							{/* Only where a gap is possible at all, so the early-renewal
							    flow keeps the panel it always had. */}
							{preview.type === "after_expiry" && (
								<div className="flex items-baseline justify-between gap-4 px-4 py-2.5 border-b border-dashed border-semantic-border-primary">
									<dt className="text-sm text-semantic-text-secondary">
										Gap in coverage
									</dt>
									<dd className="text-sm font-bold tabular-nums text-right">
										{/* Both ends carry the year: a gap can span new year's
										    eve, and "01 Jul → 10 Sep 2026" reads as if it
										    started in 2026 when it started in 2025. */}
										{preview.gap
											? `${preview.gap.days} days · ${format(
													preview.gap.from,
													"dd MMM yyyy",
												)} → ${format(preview.gap.to, "dd MMM yyyy")}`
											: "None"}
									</dd>
								</div>
							)}
							<div className="flex items-baseline justify-between gap-4 px-4 py-2.5 border-b border-dashed border-semantic-border-primary">
								<dt className="text-sm text-semantic-text-secondary">
									This subscription
								</dt>
								<dd>
									<StatusBadge variant="neutral" label="Renewed" withIcon={false} />
								</dd>
							</div>
							<div className="flex items-baseline justify-between gap-4 px-4 py-2.5 border-b border-dashed border-semantic-border-primary">
								<dt className="text-sm text-semantic-text-secondary">
									New subscription
								</dt>
								<dd>
									<StatusBadge variant={newBadge.variant} label={newBadge.label} />
								</dd>
							</div>
							<div className="flex items-baseline justify-between gap-4 px-4 py-2.5">
								<dt className="text-sm text-semantic-text-secondary">
									Renewals after this
								</dt>
								<dd className="text-sm font-bold tabular-nums">
									{subscription.renewalCount + 1}
								</dd>
							</div>
						</dl>
						<p className="px-4 py-3 border-t border-semantic-border-primary bg-semantic-bg-primary text-sm text-semantic-text-secondary">
							{preview.type === "early" &&
								`Coverage moves from ${formatSubscriptionDate(
									subscription.effectiveEndDate
								)} to ${format(
									preview.newEffectiveEnd,
									"dd MMM yyyy"
								)} without interrupting the service.`}
							{preview.type === "same_day" &&
								"The current term ends today, so the new one starts immediately."}
							{preview.type === "after_expiry" &&
								(preview.startFrom === "term_end"
									? `The term is dated to the period that was agreed instead of to today: coverage continues from ${format(
											preview.newStart,
											"dd MMM yyyy"
										)} with no gap and no day billed twice.`
									: `This subscription lapsed on ${formatSubscriptionDate(
											subscription.contractEnd
										)}. The new term starts today; the gap in coverage stays in the audit trail.`)}
						</p>
					</div>
				</div>

				{/* Refused client-side for the same reason the backend refuses it:
				    a term that closed before today would mark this subscription
				    renewed and leave the service reading as covered by nothing. */}
				{preview.termAlreadyEnded && (
					<p className="rounded-sm border border-semantic-border-danger-primary bg-semantic-bg-danger-secondary px-4 py-3 text-sm text-semantic-text-danger">
						That term ended on {format(preview.newEnd, "dd MMM yyyy")}, before
						today. Pick a longer term, or start the term today.
					</p>
				)}

				{error && (
					<p className="rounded-sm border border-semantic-border-danger-primary bg-semantic-bg-danger-secondary px-4 py-3 text-sm text-semantic-text-danger">
						{error}
					</p>
				)}
			</div>
		</GenericModal>
	);
};

export default RenewSubscriptionModal;
